import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radar, Plane, Ship, RefreshCw, Loader2, MapPin, Gauge, Compass, Mountain, History, ArrowRight, KeyRound, Check, Crosshair, PlaneTakeoff, Trash2, Search } from "lucide-react";

const fmtDate = (ts) => (ts ? new Date(ts * 1000).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDur = (m) => (m == null ? "" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

const KIND_COLOR = { Helicopter: "#F59E0B", Aeroplane: "#0EA5E9", Drone: "#A855F7", Glider: "#10B981", "Ground vehicle": "#94A3B8", Obstacle: "#94A3B8" };
const kindColor = (kind, onGround) => (onGround ? "#94A3B8" : (KIND_COLOR[kind] || "#0EA5E9"));

// Colour live aircraft by altitude: grey on ground, red below 1000 ft, yellow at/above 1000 ft.
const aircraftColor = (a = {}) => {
  if (a.on_ground) return "#94A3B8";
  let ft = a.altitude_ft;
  if (ft == null && a.geo_altitude != null) ft = a.geo_altitude * 3.28084;
  if (ft == null) return "#F59E0B";
  return ft < 1000 ? "#EF4444" : "#F59E0B";
};

const KIND_SVG = {
  Aeroplane: (c) => `<path d="M12 2c.5 0 .9.6.9 1.6v4.9l8.1 4.8v1.7l-8.1-2.4v4.6l2.4 1.8v1.4L12 20.4l-3.3 1.4v-1.4l2.4-1.8v-4.6L3 15v-1.7l8.1-4.8V3.6C11.1 2.6 11.5 2 12 2z" fill="${c}" stroke="#fff" stroke-width="0.7"/>`,
  Glider: (c) => `<path d="M12 3c.4 0 .7.5.7 1.3v3.9l9.3 2.3v1.3l-9.3-1v5.1l2 1.5v1.1L12 19.1l-2.7 1.4v-1.1l2-1.5v-5.1l-9.3 1V10.5L11.3 8.2V4.3C11.3 3.5 11.6 3 12 3z" fill="${c}" stroke="#fff" stroke-width="0.7"/>`,
  Helicopter: (c) => `<g stroke="${c}" stroke-width="1.8" stroke-linecap="round"><line x1="3.5" y1="5.5" x2="20.5" y2="18.5"/><line x1="20.5" y1="5.5" x2="3.5" y2="18.5"/></g><ellipse cx="12" cy="11" rx="3" ry="4" fill="${c}" stroke="#fff" stroke-width="0.7"/><rect x="11.4" y="10" width="1.2" height="10" rx="0.6" fill="${c}"/>`,
  Drone: (c) => `<g fill="${c}" stroke="#fff" stroke-width="0.6"><circle cx="5.5" cy="5.5" r="2.8"/><circle cx="18.5" cy="5.5" r="2.8"/><circle cx="5.5" cy="18.5" r="2.8"/><circle cx="18.5" cy="18.5" r="2.8"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/></g>`,
  "Ground vehicle": (c) => `<rect x="1.5" y="8" width="10.5" height="6" rx="0.8" fill="${c}" stroke="#fff" stroke-width="0.6"/><path d="M12 9.5h4.3l2.9 3v1.5H12z" fill="${c}" stroke="#fff" stroke-width="0.6"/><circle cx="5" cy="15" r="1.9" fill="#1f2937" stroke="#fff" stroke-width="0.5"/><circle cx="15.5" cy="15" r="1.9" fill="#1f2937" stroke="#fff" stroke-width="0.5"/>`,
};
const ROTATE_KINDS = new Set(["Aeroplane", "Helicopter", "Drone", "Glider"]);

const kindIcon = (kind = "Aeroplane", heading = 0, color = "#0EA5E9", size = 24) => {
  const svg = (KIND_SVG[kind] || KIND_SVG.Aeroplane)(color);
  const rot = ROTATE_KINDS.has(kind) ? heading : 0;
  return L.divIcon({
    className: "",
    html: `<div style="transform:rotate(${rot}deg);transition:transform .5s ease"><svg width="${size}" height="${size}" viewBox="0 0 24 24" shape-rendering="geometricPrecision" style="display:block;overflow:visible;filter:drop-shadow(0 1px 1px rgba(0,0,0,.35))">${svg}</svg></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2 + 2],
  });
};

const shipIcon = () => L.divIcon({
  className: "",
  html: `<div style="background:#0EA5E9;border:2px solid #0F172A;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.35)"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18l2-6h14l2 6"/><path d="M12 3v9"/><path d="M8 12V7h8v5"/></svg></div>`,
  iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14],
});

const pinIcon = () => L.divIcon({
  className: "",
  html: `<div style="filter:drop-shadow(0 2px 3px rgba(0,0,0,.4))"><svg width="30" height="30" viewBox="0 0 24 24" fill="#84CC16" stroke="#0F172A" stroke-width="1.2"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/><circle cx="12" cy="9" r="2.4" fill="#0F172A"/></svg></div>`,
  iconSize: [30, 30], iconAnchor: [15, 28], popupAnchor: [0, -26],
});

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => { if (position) map.flyTo(position, Math.max(map.getZoom(), 8), { duration: 1 }); }, [position]); // eslint-disable-line
  return null;
}

function MapRefSetter({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

function ClickCapture({ active, onPick }) {
  useMapEvents({ click(e) { if (active) onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-sm">
    <Icon className="h-3.5 w-3.5 text-lime-600 shrink-0" />
    <span className="text-slate-500">{label}:</span>
    <span className="font-medium text-slate-900">{value}</span>
  </div>
);

export default function TrackingPage() {
  const [searchParams] = useSearchParams();
  const [acQuery, setAcQuery] = useState("");
  const [shipQuery, setShipQuery] = useState("");
  const [aircraft, setAircraft] = useState(null);
  const [track, setTrack] = useState([]);
  const [liveTrail, setLiveTrail] = useState([]);   // accumulating live flight-progress breadcrumbs
  const [ship, setShip] = useState(null);
  const [loadingAc, setLoadingAc] = useState(false);
  const [loadingShip, setLoadingShip] = useState(false);
  const [auto, setAuto] = useState(false);
  const [focus, setFocus] = useState(null);
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [flights, setFlights] = useState(null);
  const [loadingFlights, setLoadingFlights] = useState(false);
  const [activeFlight, setActiveFlight] = useState(null);
  const [keys, setKeys] = useState(null);          // null = not admin / hidden
  const [showAcKeys, setShowAcKeys] = useState(false);
  const [showShipKeys, setShowShipKeys] = useState(false);
  const [keyForm, setKeyForm] = useState({ opensky_client_id: "", opensky_client_secret: "", aisstream_api_key: "" });
  const [savingKeys, setSavingKeys] = useState(false);
  const assetIdRef = useRef(null);
  const timer = useRef(null);
  const mapRef = useRef(null);
  const [nearby, setNearby] = useState(null);
  const [nearbyForm, setNearbyForm] = useState({ lat: "", lon: "", radius: "50" });
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [areaShips, setAreaShips] = useState(null);
  const [zones, setZones] = useState([]);
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: "", kind: "aircraft", lat: "", lon: "", radius: "25" });
  const [busyZone, setBusyZone] = useState(null);
  const [pinMode, setPinMode] = useState(false);
  const [pinTarget, setPinTarget] = useState("zone");   // which form a dropped pin fills
  const [geoQuery, setGeoQuery] = useState("");
  const [geoResults, setGeoResults] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const markerRefs = useRef({});

  const focusItem = (id, lat, lon) => {
    if (mapRef.current) mapRef.current.flyTo([lat, lon], Math.max(mapRef.current.getZoom(), 10), { duration: 0.8 });
    const m = markerRefs.current[id];
    if (m) setTimeout(() => m.openPopup(), 450);
  };
  const onPickPin = (lat, lon) => {
    const setter = pinTarget === "nearby" ? setNearbyForm : setZoneForm;
    setter((f) => ({ ...f, lat: lat.toFixed(4), lon: lon.toFixed(4) }));
    setPinMode(false);
    toast.success("Pin dropped — location set");
  };

  const runGeocode = async () => {
    if (!geoQuery.trim()) return;
    setGeoLoading(true); setGeoResults([]);
    try {
      const r = await api.get("/tracking/geocode", { params: { q: geoQuery.trim() } });
      if (!r.data.results?.length) toast.info("No matching location found — try adding a state/country");
      setGeoResults(r.data.results || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Location search failed"); }
    finally { setGeoLoading(false); }
  };
  const pickGeo = (res) => {
    setNearbyForm((f) => ({ ...f, lat: res.lat.toFixed(4), lon: res.lon.toFixed(4) }));
    setGeoResults([]); setGeoQuery(res.name.split(",")[0]);
    if (mapRef.current) mapRef.current.flyTo([res.lat, res.lon], 9, { duration: 0.8 });
    toast.success("Location set — click Scan traffic");
  };

  const loadZones = useCallback(() => {
    api.get("/tracking/zones").then((r) => setZones(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadZones(); }, [loadZones]);

  const scanAircraftArea = async (lat, lon, radius) => {
    const r = await api.get("/tracking/nearby", { params: { lat, lon, radius } });
    setNearby(r.data); setAreaShips(null);
    if (mapRef.current) mapRef.current.flyTo([lat, lon], radius > 100 ? 6 : 8, { duration: 1 });
    toast.success(`${r.data.count} aircraft within ${radius} nm`);
  };
  const scanShipArea = async (lat, lon, radius) => {
    const r = await api.get("/tracking/ships-area", { params: { lat, lon, radius } });
    setAreaShips(r.data); setNearby(null);
    if (mapRef.current) mapRef.current.flyTo([lat, lon], radius > 60 ? 7 : 9, { duration: 1 });
    toast.success(`${r.data.count} vessels within ${radius} nm`);
  };

  const scanNearby = async () => {
    const lat = parseFloat(nearbyForm.lat), lon = parseFloat(nearbyForm.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) { toast.error("Enter a valid latitude & longitude"); return; }
    setLoadingNearby(true);
    try { await scanAircraftArea(lat, lon, parseInt(nearbyForm.radius, 10)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Traffic scan failed"); }
    finally { setLoadingNearby(false); }
  };
  const applyMapCentre = (setter) => {
    if (!mapRef.current) return;
    const c = mapRef.current.getCenter();
    setter((f) => ({ ...f, lat: c.lat.toFixed(4), lon: c.lng.toFixed(4) }));
  };

  const scanZone = async (z) => {
    setBusyZone(z.id);
    try {
      if (z.kind === "ship") await scanShipArea(z.lat, z.lon, z.radius);
      else await scanAircraftArea(z.lat, z.lon, z.radius);
    } catch (e) { toast.error(e?.response?.data?.detail || "Zone scan failed"); }
    finally { setBusyZone(null); }
  };
  const saveZone = async () => {
    const lat = parseFloat(zoneForm.lat), lon = parseFloat(zoneForm.lon);
    if (!zoneForm.name.trim() || Number.isNaN(lat) || Number.isNaN(lon)) { toast.error("Enter a name and valid coordinates"); return; }
    try {
      await api.post("/tracking/zones", { name: zoneForm.name.trim(), kind: zoneForm.kind, lat, lon, radius: parseInt(zoneForm.radius, 10) });
      toast.success("Zone saved");
      setZoneForm({ name: "", kind: "aircraft", lat: "", lon: "", radius: "25" });
      setShowZoneForm(false); loadZones();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteZone = async (id) => {
    try { await api.delete(`/tracking/zones/${id}`); setZones((z) => z.filter((x) => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  const loadKeys = useCallback(() => {
    api.get("/tracking/keys").then((r) => setKeys(r.data)).catch(() => setKeys(null));
  }, []);
  useEffect(() => { loadKeys(); }, [loadKeys]);

  const saveKeys = async (fields) => {
    const body = {};
    fields.forEach((f) => { if (keyForm[f]?.trim()) body[f] = keyForm[f].trim(); });
    if (Object.keys(body).length === 0) { toast.error("Enter a key first"); return; }
    setSavingKeys(true);
    try {
      await api.post("/tracking/keys", body);
      toast.success("Key saved & encrypted");
      setKeyForm({ opensky_client_id: "", opensky_client_secret: "", aisstream_api_key: "" });
      setShowAcKeys(false); setShowShipKeys(false);
      loadKeys();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingKeys(false); }
  };
  const setKf = (k, v) => setKeyForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/tracking/assets").then((r) => setAssets(r.data || [])).catch(() => {});
  }, []);

  const loadFlights = useCallback(async (q) => {
    setLoadingFlights(true); setFlights(null); setActiveFlight(null);
    try {
      const r = await api.get("/tracking/aircraft/flights", { params: { q, days: 30 } });
      setFlights(r.data);
    } catch { setFlights({ flights: [] }); } finally { setLoadingFlights(false); }
  }, []);

  const showFlightTrack = useCallback(async (f, icao24) => {
    setActiveFlight(f.first_seen);
    try {
      const t = await api.get("/tracking/aircraft/track", { params: { icao24, time: (f.first_seen || 0) + 60 } });
      const pts = (t.data.path || []).map((p) => [p.lat, p.lon]);
      setTrack(pts);
      if (pts.length) setFocus(pts[0]);
      if (!pts.length) toast.info("No detailed track available for that flight.");
    } catch { toast.error("Could not load that flight's track."); }
  }, []);

  const fetchAircraft = useCallback(async (q, silent = false) => {
    if (!q?.trim()) return;
    if (!silent) setLoadingAc(true);
    try {
      const r = await api.get("/tracking/aircraft", { params: { q, asset_id: assetIdRef.current || undefined } });
      setAircraft(r.data);
      if (!silent) loadFlights(q);
      setTrack([]);
      if (r.data.found) {
        const pt = [r.data.lat, r.data.lon];
        setFocus(pt);
        if (silent) {
          setLiveTrail((tr) => {
            const last = tr[tr.length - 1];
            if (last && last[0] === pt[0] && last[1] === pt[1]) return tr;
            return [...tr, pt];
          });
        } else {
          setLiveTrail([pt]);
          setAuto(true);   // start live tracking automatically
        }
      } else if (!silent) {
        setLiveTrail([]);
        toast.info(r.data.message || "No live position");
      }
    } catch (e) {
      setAircraft(null); setTrack([]);
      toast.error(e?.response?.data?.detail || "Aircraft lookup failed");
    } finally { setLoadingAc(false); }
  }, [loadFlights]);

  const pickAsset = (assetId) => {
    setSelectedAsset(assetId);
    const a = assets.find((x) => x.id === assetId);
    if (!a) return;
    const code = a.icao24 || a.registration;
    assetIdRef.current = a.id;
    setAcQuery(code);
    fetchAircraft(code);
  };

  const onAcInput = (v) => { setAcQuery(v); assetIdRef.current = null; setSelectedAsset(""); };

  const fetchShip = useCallback(async (q) => {
    if (!q?.trim()) return;
    setLoadingShip(true);
    try {
      const r = await api.get("/tracking/ship", { params: { q } });
      setShip(r.data);
      if (r.data.found) setFocus([r.data.lat, r.data.lon]);
      else toast.info(r.data.message || "No live position");
    } catch (e) {
      setShip(null);
      toast.error(e?.response?.data?.detail || "Ship lookup failed");
    } finally { setLoadingShip(false); }
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) { setAcQuery(q); fetchAircraft(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (auto && aircraft?.found) {
      timer.current = setInterval(() => fetchAircraft(acQuery, true), 15000);
    }
    return () => timer.current && clearInterval(timer.current);
  }, [auto, aircraft?.found, acQuery, fetchAircraft]);

  return (
    <div className="space-y-6 fade-up" data-testid="tracking-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Fleet &amp; Equipment</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
          <Radar className="h-8 w-8 text-lime-500" /> Asset Tracking
        </h1>
        <p className="text-slate-500 mt-1">Track live aircraft positions (via the ADS-B network) and current vessel positions (via AIS).</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Aircraft */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="aircraft-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Plane className="h-5 w-5 text-slate-900" />
              <h2 className="font-heading font-semibold text-slate-900">Aircraft</h2>
            </div>
            {keys && (
              <button data-testid="aircraft-keys-toggle" onClick={() => setShowAcKeys((s) => !s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900">
                <KeyRound className="h-3.5 w-3.5" />
                {keys.opensky_client_id?.set && keys.opensky_client_secret?.set ? <span className="text-lime-600 inline-flex items-center gap-1"><Check className="h-3 w-3" /> Key set</span> : "Set key"}
              </button>
            )}
          </div>
          {showAcKeys && keys && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="aircraft-keys-form">
              <p className="text-xs text-slate-500">OpenSky API (encrypted &amp; saved to the live site).</p>
              <Input data-testid="opensky-id-input" placeholder={keys.opensky_client_id?.set ? `Client ID (${keys.opensky_client_id.hint})` : "OpenSky Client ID"} value={keyForm.opensky_client_id} onChange={(e) => setKf("opensky_client_id", e.target.value)} />
              <Input data-testid="opensky-secret-input" type="password" placeholder={keys.opensky_client_secret?.set ? `Client Secret (${keys.opensky_client_secret.hint})` : "OpenSky Client Secret"} value={keyForm.opensky_client_secret} onChange={(e) => setKf("opensky_client_secret", e.target.value)} />
              <Button size="sm" data-testid="save-aircraft-key-btn" disabled={savingKeys} onClick={() => saveKeys(["opensky_client_id", "opensky_client_secret"])} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                {savingKeys ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Key"}
              </Button>
            </div>
          )}
          {assets.length > 0 && (
            <div className="mb-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Pick one of your assets</Label>
              <Select value={selectedAsset} onValueChange={pickAsset}>
                <SelectTrigger className="mt-1.5" data-testid="asset-select"><SelectValue placeholder="Select an aircraft…" /></SelectTrigger>
                <SelectContent>
                  {assets.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`asset-option-${a.id}`}>
                      {a.name}{a.registration ? ` · ${a.registration}` : ""}{a.icao24 ? ` · ${a.icao24.toUpperCase()}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">…or enter a registration / ICAO24 hex</Label>
          <div className="flex gap-2 mt-1.5">
            <Input data-testid="aircraft-input" value={acQuery} placeholder="e.g. VH-RNX or 7C6C6E"
              onChange={(e) => onAcInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchAircraft(acQuery)} />
            <Button data-testid="track-aircraft-btn" onClick={() => fetchAircraft(acQuery)} disabled={loadingAc}
              className="bg-slate-900 hover:bg-slate-800 text-white shrink-0">
              {loadingAc ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Track</>}
            </Button>
          </div>
          {aircraft && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="aircraft-result">
              {aircraft.found ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-semibold text-slate-900">
                      {aircraft.registration || aircraft.icao24?.toUpperCase()}
                      {aircraft.callsign ? <span className="text-slate-400 font-normal"> · {aircraft.callsign}</span> : null}
                    </p>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" title="Refreshes the position every 15s and draws the flight-progress trail">
                      <input type="checkbox" data-testid="auto-refresh-toggle" checked={auto}
                        onChange={(e) => setAuto(e.target.checked)} className="accent-lime-500" />
                      Live track
                    </label>
                  </div>
                  <Stat icon={MapPin} label="Position" value={`${aircraft.lat?.toFixed(3)}, ${aircraft.lon?.toFixed(3)}`} />
                  <Stat icon={Plane} label="Type" value={`${aircraft.kind || "—"}${aircraft.aircraft_type ? ` · ${aircraft.aircraft_type}` : ""}`} />
                  <Stat icon={Mountain} label="Altitude" value={aircraft.geo_altitude != null ? `${Math.round(aircraft.geo_altitude)} m` : (aircraft.on_ground ? "On ground" : "—")} />
                  <Stat icon={Gauge} label="Speed" value={aircraft.velocity != null ? `${Math.round(aircraft.velocity * 1.94384)} kt` : "—"} />
                  <Stat icon={Compass} label="Heading" value={aircraft.heading != null ? `${Math.round(aircraft.heading)}°` : "—"} />
                  <p className="text-xs text-slate-400">{aircraft.origin_country}{aircraft.on_ground ? " · on ground" : ""}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="aircraft-not-found">{aircraft.message}</p>
              )}
            </div>
          )}
        </div>

        {/* Ship */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ship-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Ship className="h-5 w-5 text-slate-900" />
              <h2 className="font-heading font-semibold text-slate-900">Ship</h2>
            </div>
            {keys && (
              <button data-testid="ship-keys-toggle" onClick={() => setShowShipKeys((s) => !s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900">
                <KeyRound className="h-3.5 w-3.5" />
                {keys.aisstream_api_key?.set ? <span className="text-lime-600 inline-flex items-center gap-1"><Check className="h-3 w-3" /> Key set</span> : "Set key"}
              </button>
            )}
          </div>
          {showShipKeys && keys && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="ship-keys-form">
              <p className="text-xs text-slate-500">AISstream.io API key (encrypted &amp; saved to the live site).</p>
              <Input data-testid="aisstream-key-input" type="password" placeholder={keys.aisstream_api_key?.set ? `API key (${keys.aisstream_api_key.hint})` : "AISstream API key"} value={keyForm.aisstream_api_key} onChange={(e) => setKf("aisstream_api_key", e.target.value)} />
              <Button size="sm" data-testid="save-ship-key-btn" disabled={savingKeys} onClick={() => saveKeys(["aisstream_api_key"])} className="w-full bg-sky-600 hover:bg-sky-500 text-white">
                {savingKeys ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Key"}
              </Button>
            </div>
          )}
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Vessel MMSI (9 digits)</Label>
          <div className="flex gap-2 mt-1.5">
            <Input data-testid="ship-input" value={shipQuery} placeholder="e.g. 503000000"
              onChange={(e) => setShipQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchShip(shipQuery)} />
            <Button data-testid="track-ship-btn" onClick={() => fetchShip(shipQuery)} disabled={loadingShip}
              className="bg-sky-600 hover:bg-sky-500 text-white shrink-0">
              {loadingShip ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Track</>}
            </Button>
          </div>
          {ship && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="ship-result">
              {ship.found ? (
                <div className="space-y-1.5">
                  <p className="font-heading font-semibold text-slate-900">{ship.name || `MMSI ${ship.mmsi}`}</p>
                  <Stat icon={MapPin} label="Position" value={`${ship.lat?.toFixed(3)}, ${ship.lon?.toFixed(3)}`} />
                  <Stat icon={Gauge} label="Speed" value={ship.sog != null ? `${ship.sog} kn` : "—"} />
                  <Stat icon={Compass} label="Course" value={ship.cog != null ? `${Math.round(ship.cog)}°` : "—"} />
                  {ship.timestamp && <p className="text-xs text-slate-400">{ship.timestamp}</p>}
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="ship-not-found">{ship.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Flight history */}
      {(loadingFlights || flights) && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="flight-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-5 w-5 text-slate-900" />
            <h2 className="font-heading font-semibold text-slate-900">Flight history <span className="text-slate-400 font-normal text-sm">· last 30 days</span></h2>
            {loadingFlights && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          {loadingFlights ? (
            <p className="text-sm text-slate-400">Fetching past flights… this can take a few seconds.</p>
          ) : flights?.unreachable ? (
            <p className="text-sm text-slate-500" data-testid="flight-history-unavailable">Flight history is temporarily unavailable (the historical flights provider, OpenSky, can't be reached right now). Live position tracking above is unaffected.</p>
          ) : (flights?.flights || []).length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="flight-history-empty">No recorded flights in the last 30 days. (OpenSky only lists flights seen by its ADS-B receiver network, and today's flights appear after nightly processing.)</p>
          ) : (
            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="flight-history-list">
              {flights.flights.map((f, i) => (
                <button key={i} onClick={() => showFlightTrack(f, flights.icao24)} data-testid={`flight-row-${i}`}
                  className={`w-full text-left py-2.5 px-2 -mx-2 rounded-lg flex items-center gap-3 hover:bg-slate-50 transition-colors ${activeFlight === f.first_seen ? "bg-lime-50" : ""}`}>
                  <Plane className="h-4 w-4 text-lime-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                      <span>{f.from || "—"}</span><ArrowRight className="h-3.5 w-3.5 text-slate-400" /><span>{f.to || "—"}</span>
                      {f.callsign && <span className="text-slate-400 font-normal">· {f.callsign}</span>}
                    </div>
                    <p className="text-xs text-slate-400">{fmtDate(f.first_seen)}{f.duration_min ? ` · ${fmtDur(f.duration_min)}` : ""}</p>
                  </div>
                  <span className="text-xs text-lime-600 shrink-0">View path</span>
                </button>
              ))}
              {flights.partial && <p className="text-xs text-amber-600 pt-2">Some days couldn't be fetched (rate limit / timeout) — try again later for the full history.</p>}
            </div>
          )}
        </div>
      )}

      {/* Nearby air traffic */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="nearby-panel">
        <div className="flex items-center gap-2 mb-3">
          <PlaneTakeoff className="h-5 w-5 text-slate-900" />
          <h2 className="font-heading font-semibold text-slate-900">Nearby air traffic <span className="text-slate-400 font-normal text-sm">· airspace awareness</span></h2>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-32"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Latitude</Label>
            <Input data-testid="nearby-lat" className="mt-1.5" placeholder="-33.87" value={nearbyForm.lat} onChange={(e) => setNearbyForm((f) => ({ ...f, lat: e.target.value }))} /></div>
          <div className="w-32"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Longitude</Label>
            <Input data-testid="nearby-lon" className="mt-1.5" placeholder="151.21" value={nearbyForm.lon} onChange={(e) => setNearbyForm((f) => ({ ...f, lon: e.target.value }))} /></div>
          <div className="w-32"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Radius</Label>
            <Select value={nearbyForm.radius} onValueChange={(v) => setNearbyForm((f) => ({ ...f, radius: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="nearby-radius"><SelectValue /></SelectTrigger>
              <SelectContent>{["10", "25", "50", "100", "250"].map((r) => <SelectItem key={r} value={r}>{r} nm</SelectItem>)}</SelectContent>
            </Select></div>
          <Button variant="outline" data-testid="use-map-centre-btn" onClick={() => applyMapCentre(setNearbyForm)}><Crosshair className="h-4 w-4 mr-1.5" /> Use map centre</Button>
          <Button data-testid="scan-nearby-btn" onClick={scanNearby} disabled={loadingNearby} className="bg-slate-900 hover:bg-slate-800 text-white">
            {loadingNearby ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Radar className="h-4 w-4 mr-1.5" /> Scan traffic</>}
          </Button>
          {nearby && <span className="text-sm text-slate-500" data-testid="nearby-count"><b className="text-slate-900">{nearby.count}</b> aircraft within {nearby.radius} nm</span>}
        </div>
      </div>

      {/* Tracking zones */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="zones-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-5 w-5 text-lime-500" /> Tracking zones</h2>
          <Button size="sm" variant="outline" data-testid="add-zone-btn" onClick={() => setShowZoneForm((s) => !s)}>{showZoneForm ? "Cancel" : "+ Add zone"}</Button>
        </div>
        {showZoneForm && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-end gap-2" data-testid="zone-form">
            <div className="w-48"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input data-testid="zone-name" className="mt-1.5" placeholder="e.g. Bankstown Airport" value={zoneForm.name} onChange={(e) => setZoneForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="w-36"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={zoneForm.kind} onValueChange={(v) => setZoneForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="zone-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aircraft">Aircraft (airfield/suburb)</SelectItem>
                  <SelectItem value="ship">Shipping (port)</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="w-28"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Latitude</Label>
              <Input data-testid="zone-lat" className="mt-1.5" value={zoneForm.lat} onChange={(e) => setZoneForm((f) => ({ ...f, lat: e.target.value }))} /></div>
            <div className="w-28"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Longitude</Label>
              <Input data-testid="zone-lon" className="mt-1.5" value={zoneForm.lon} onChange={(e) => setZoneForm((f) => ({ ...f, lon: e.target.value }))} /></div>
            <div className="w-24"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Radius</Label>
              <Select value={zoneForm.radius} onValueChange={(v) => setZoneForm((f) => ({ ...f, radius: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="zone-radius"><SelectValue /></SelectTrigger>
                <SelectContent>{["5", "10", "25", "50", "100"].map((r) => <SelectItem key={r} value={r}>{r} nm</SelectItem>)}</SelectContent>
              </Select></div>
            <Button variant="outline" onClick={() => applyMapCentre(setZoneForm)}><Crosshair className="h-4 w-4 mr-1.5" /> Map centre</Button>
            <Button variant={pinMode && pinTarget === "zone" ? "default" : "outline"} data-testid="drop-pin-btn" onClick={() => { setPinTarget("zone"); setPinMode((p) => !(p && pinTarget === "zone")); if (!(pinMode && pinTarget === "zone")) toast.info("Click the map below to drop the pin"); }} className={pinMode && pinTarget === "zone" ? "bg-lime-500 hover:bg-lime-400 text-slate-900" : ""}><MapPin className="h-4 w-4 mr-1.5" /> {pinMode && pinTarget === "zone" ? "Click map…" : "Drop pin"}</Button>
            <Button data-testid="save-zone-btn" onClick={saveZone} className="bg-slate-900 hover:bg-slate-800 text-white">Save zone</Button>
          </div>
        )}
        {zones.length === 0 ? (
          <p className="text-sm text-slate-400">No zones yet. Add airfields or suburbs to watch aircraft, or ports to watch shipping.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="zones-list">
            {zones.map((z) => (
              <div key={z.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5" data-testid={`zone-${z.id}`}>
                {z.kind === "ship" ? <Ship className="h-4 w-4 text-sky-600 shrink-0" /> : <Plane className="h-4 w-4 text-lime-600 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{z.name}</p>
                  <p className="text-xs text-slate-400">{z.radius} nm · {z.lat.toFixed(2)}, {z.lon.toFixed(2)}</p>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-lime-700 hover:bg-lime-50" data-testid={`scan-zone-${z.id}`} disabled={busyZone === z.id} onClick={() => scanZone(z)}>
                  {busyZone === z.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Radar className="h-3.5 w-3.5 mr-1" /> Scan</>}
                </Button>
                <button className="text-slate-300 hover:text-red-600" data-testid={`delete-zone-${z.id}`} onClick={() => deleteZone(z.id)}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        {areaShips && <p className="text-sm text-slate-500 mt-3" data-testid="area-ships-count"><b className="text-slate-900">{areaShips.count}</b> vessels within {areaShips.radius} nm</p>}
      </div>

      {/* Scan results list */}
      {(nearby || areaShips) && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="scan-results">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">
            In range: {nearby ? `${nearby.count} aircraft` : `${areaShips.count} vessels`}
          </h2>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="scan-results-list">
            {nearby && nearby.aircraft.map((a) => (
              <div key={a.icao24} onClick={() => focusItem(a.icao24, a.lat, a.lon)} className="py-2 flex items-center gap-3 text-sm cursor-pointer hover:bg-slate-50 rounded-lg px-1 -mx-1" data-testid={`result-ac-${a.icao24}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: kindColor(a.kind, a.on_ground) }} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">
                    {a.registration || a.callsign || a.icao24?.toUpperCase()}
                    <span className="text-slate-400 font-normal"> · {a.kind || "Aircraft"}{a.type ? ` (${a.type})` : ""}</span>
                  </p>
                  <p className="text-xs text-slate-400">{a.callsign && (a.registration) ? `${a.callsign} · ` : ""}{a.on_ground ? "on ground" : `${a.altitude_ft ?? "—"} ft`}{a.speed_kt != null ? ` · ${Math.round(a.speed_kt)} kt` : ""}</p>
                </div>
                {a.distance_nm != null && <span className="text-xs text-slate-500 shrink-0">{a.distance_nm} nm</span>}
              </div>
            ))}
            {areaShips && areaShips.ships.map((s) => (
              <div key={s.mmsi} onClick={() => focusItem(`ship_${s.mmsi}`, s.lat, s.lon)} className="py-2 flex items-center gap-3 text-sm cursor-pointer hover:bg-slate-50 rounded-lg px-1 -mx-1" data-testid={`result-ship-${s.mmsi}`}>
                <Ship className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{s.name || `MMSI ${s.mmsi}`}</p>
                  <p className="text-xs text-slate-400">MMSI {s.mmsi}{s.sog != null ? ` · ${s.sog} kn` : ""}{s.cog != null ? ` · ${Math.round(s.cog)}°` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: "520px" }} data-testid="tracking-map">
        <MapContainer center={[-25.3, 133.8]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapRefSetter mapRef={mapRef} />
          <ClickCapture active={pinMode} onPick={onPickPin} />
          <FlyTo position={focus} />
          {showZoneForm && !Number.isNaN(parseFloat(zoneForm.lat)) && !Number.isNaN(parseFloat(zoneForm.lon)) && (
            <>
              <Marker position={[parseFloat(zoneForm.lat), parseFloat(zoneForm.lon)]} icon={pinIcon()} />
              <Circle center={[parseFloat(zoneForm.lat), parseFloat(zoneForm.lon)]} radius={(parseInt(zoneForm.radius, 10) || 25) * 1852} pathOptions={{ color: "#84CC16", weight: 1.5, dashArray: "6 6", fillOpacity: 0.04 }} />
            </>
          )}
          {nearby && (
            <>
              <Circle center={[nearby.lat, nearby.lon]} radius={nearby.radius * 1852} pathOptions={{ color: "#0EA5E9", weight: 1.5, fillOpacity: 0.05 }} />
              {nearby.aircraft.map((a) => (
                <Marker key={a.icao24} position={[a.lat, a.lon]} icon={kindIcon(a.kind, a.heading || 0, aircraftColor(a))}>
                  <Popup>
                    <b>{a.registration || a.callsign || a.icao24?.toUpperCase()}</b>{a.type ? ` · ${a.type}` : ""}<br />
                    {a.kind || "Aircraft"}{a.category_label ? ` (${a.category_label})` : ""}<br />
                    {a.on_ground ? "on ground" : `${a.altitude_ft ?? "—"} ft`}
                    {a.speed_kt != null ? ` · ${Math.round(a.speed_kt)} kt` : ""}
                    {a.distance_nm != null ? <><br />{a.distance_nm} nm away</> : null}
                  </Popup>
                </Marker>
              ))}
            </>
          )}
          {areaShips && (
            <>
              <Circle center={[areaShips.lat, areaShips.lon]} radius={areaShips.radius * 1852} pathOptions={{ color: "#0284C7", weight: 1.5, fillOpacity: 0.05 }} />
              {areaShips.ships.map((s) => (
                <Marker key={s.mmsi} position={[s.lat, s.lon]} icon={shipIcon()} ref={(el) => { if (el) markerRefs.current[`ship_${s.mmsi}`] = el; }}>
                  <Popup><b>{s.name || `MMSI ${s.mmsi}`}</b><br />{s.sog != null ? `${s.sog} kn` : ""}{s.cog != null ? ` · ${Math.round(s.cog)}°` : ""}</Popup>
                </Marker>
              ))}
            </>
          )}
          {track.length > 1 && <Polyline positions={track} pathOptions={{ color: "#84CC16", weight: 3, opacity: 0.8 }} />}
          {liveTrail.length > 1 && <Polyline positions={liveTrail} pathOptions={{ color: "#84CC16", weight: 3, opacity: 0.9, dashArray: "2 7", lineCap: "round" }} />}
          {aircraft?.found && (
            <Marker position={[aircraft.lat, aircraft.lon]} icon={kindIcon(aircraft.kind || "Aeroplane", aircraft.heading || 0, aircraftColor(aircraft), 34)}>
              <Popup>
                <b>{aircraft.registration || aircraft.icao24?.toUpperCase()}</b>
                {aircraft.callsign ? ` · ${aircraft.callsign}` : ""}<br />
                {aircraft.velocity != null ? `${Math.round(aircraft.velocity * 1.94384)} kt` : ""}
                {aircraft.geo_altitude != null ? ` · ${Math.round(aircraft.geo_altitude)} m` : ""}
              </Popup>
            </Marker>
          )}
          {ship?.found && (
            <Marker position={[ship.lat, ship.lon]} icon={shipIcon()}>
              <Popup><b>{ship.name || `MMSI ${ship.mmsi}`}</b><br />{ship.sog != null ? `${ship.sog} kn` : ""}</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
